import { SeverityLevel } from '@microsoft/applicationinsights-common';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useParams } from 'react-router';
import { Row, Table, Col, Collapse } from 'reactstrap';
import useGetCustomers from '../../requests/useGetCustomers';
import useSearchOrders, { OrderDisplayDto } from '../../requests/useSearchOrders';
import { customerDisplayName, customerDisplayNameFromDto, dateSort } from '../../Utilities';
import PermissionButton from '../UserAccess/PermissionButton';
import GatedButton from '../UserAccess/GatedButton';
import { usePermission } from '../UserAccess/usePermission';
import { CenteredSpinner } from '../Common/CenteredSpinner';
import Confirm from '../Common/Confirm';
import OrderDto from '../Common/dtos/OrderDto';
import ProductDto from '../Common/dtos/ProductDto';
import RecipeDto from '../Common/dtos/RecipeDto';
import KaAlert from '../Common/KaAlert';
import { KaSelectOption, toKaSelectOptions } from '../Common/KaSelect';
import OverflowTooltip from '../Common/OverflowTooltip';
import PaginatedTable from '../Common/Pagination/PaginatedTable';
import QueryView from '../Common/QueryView';
import { SiteAssignmentForm } from '../Common/SiteAssignment/SiteAssignmentForm';
import useDisplayMessage from '../Common/useDisplayMessage';
import useToggleableItems from '../Common/useToggleableItems';
import { HttpMethod } from '../Common/useFetch';
import useUserRequest, { UserRequestSpec } from '../Common/useUserRequest';
import BoldButton from '../Common/BoldButton';
import FilterInput from '../FilterInput';
import Toggle from '../Common/Toggle';
import SelectPill from '../Common/Filters/SelectPill';
import '../Common/Filters/FilterPill.css';
import DateRangePill from '../Common/Filters/DateRangePill';
import FilterPillGroup from '../Common/Filters/FilterPillGroup';
import { SiteFilterPill, useSiteFilter } from '../UserAccess/SiteScope/SiteFilterPill';
import { SiteColumnCell, SiteColumnHeader } from '../Common/SiteColumn';
import { useLogException } from '../Monitoring/useMonitoring';
import useGetProducts from '../Requests/useGetProducts';
import useGetRecipes from '../Requests/useGetRecipes';
import { useUser } from '../UserAccess/useUser';
import { CreateEditMode, CreateEditOrder } from './CreateEditOrder';
import { OrderTransactionPage } from './Transactions/OrderTransactionPage';
import CreateTransactionModal from './CreateTransactionModal';
import useOrderTransactionsByOrder from '../../requests/useOrderTransactionsByOrder';
import OrderTransaction, { TransactionStatus } from '../Common/dtos/OrderTransactionDto';
import { OptionalAmount } from '../Common/dtos/OptionalAmount';
import { AmountDisplay } from '../Common/AmountDisplay';
import { SortableColumnHeader, SortDirection } from '../Common/SortableColumnHeader';
import useBulkRemoveOrders, { BulkRemoveOrdersResponse } from '../../requests/useBulkRemoveOrders';
import useBulkCompleteOrders, { BulkCompleteOrdersResponse } from '../../requests/useBulkCompleteOrders';

interface AssignSitesOrderDto {
  id: string;
  number: string;
}

type FilterSettings = {
  item: string;
  customer: string;
  startDate: string;
  endDate: string;
}

type SortMode =
  { isHighlighted: boolean, field: SortField, direction: SortDirection }

enum SortField {
  number,
  createdAt,
  completedAt,
}

const defaultSort: SortMode = ({
  isHighlighted: false,
  field: SortField.number,
  direction: SortDirection.ascending
});

type ShowTransactions =
  undefined | { order: OrderDto, orderTransactionId?: string };

const apiPath = (businessId?: string): string => `/api/businesses/${businessId}/orders`;

const completeRequestSpec = (businessId?: string, orderId?: string): UserRequestSpec => ({
  path: `${apiPath(businessId)}/${orderId}/complete`,
  method: HttpMethod.POST,
});

const removeRequestSpec = (businessId?: string, orderId?: string): UserRequestSpec => ({
  path: `${apiPath(businessId)}/${orderId}`,
  method: HttpMethod.DELETE,
});

const apiGetSiteAssignmentPath = (businessId?: string, orderId?: string): string =>
  `${apiPath(businessId)}/${orderId}/siteAssignment`;

const apiSetSiteAssignmentPath = (businessId?: string, orderId?: string): string =>
  `${apiPath(businessId)}/${orderId}/assignToSites`;

const Totals = ({ requested, ots, orderId }: { requested: OptionalAmount, ots: OrderTransaction[], orderId: string }) => {
  const error = requested.error
    ?? ots.find(ot => ot.totalDispensed.error)?.totalDispensed.error;

  if (error) return <Col xs={{ size: 3, offset: 9 }}>
    <span className="text-primary fw-semibold">Order Totals:  </span>
    <AmountDisplay amount={{ error, unit: requested.unit }} hoverId="totals-error" />
  </Col>

  const completedOts = ots.filter(ot => ot.status === TransactionStatus.Completed);
  const totalDispensed = completedOts
    .map(ot => ot.totalDispensed.quantity!)
    .reduce((sum, n) => sum + n, 0);

  const completionPercentage = requested.quantity === 0
    ? '0'
    : Math.min(totalDispensed / requested.quantity! * 100, 100).toFixed(1);

  return <Col xs={{ size: 6, offset: 5 }}>
    <div>
      <small className="text-muted">Dispensed / Requested</small>
    </div>
    <div className="d-flex justify-content-between">
      <div>
        <span className="fw-semibold text-primary">
          <AmountDisplay
            amount={{ quantity: totalDispensed, unit: requested.unit }}
            hoverId={`dispensed-total-${orderId}`}
          />
          {' / '}
          <AmountDisplay amount={requested} hoverId={`requested-total-${orderId}`} />
        </span>
        <span className="text-muted ms-2">({completionPercentage}%)</span>
      </div>
      <div className="mt-1">
        <span className="text-muted">Remaining: </span>
        <span className="fw-semibold text-primary">
          <AmountDisplay
            amount={{ quantity: requested.quantity! - totalDispensed, unit: requested.unit }}
            hoverId={`remaining-total-${orderId}`} />
        </span>
      </div>
    </div>
    <div className="progress mb-2" style={{ height: '8px' }}>
      <div
        className="progress-bar bg-primary"
        role="progressbar"
        style={{ width: `${completionPercentage}%` }}
        aria-valuenow={Number(completionPercentage)}
      />
    </div>
  </Col>
}

type OrderTransactionsProps = {
  orderId: string,
  requested: OptionalAmount,
  show: boolean,
  transactionSelected: (otId: string) => void,
}

const OrderTransactions = ({ orderId, requested, show, transactionSelected }: OrderTransactionsProps) => {
  const [prevShow, setPrevShow] = useState(false);
  const getOrderTransactions = useOrderTransactionsByOrder(orderId, requested.unit ?? 'Pound', { autoQuery: false });

  useEffect(() => {
    if (!prevShow && show) {
      getOrderTransactions.query();
    }

    setPrevShow(show);
  }, [show, getOrderTransactions, prevShow]);

  return <QueryView
    query={getOrderTransactions}
    renderLoading={() =>
      <td className="py-6 position-relative" colSpan={5}>
        <div className="position-absolute top-50 start-50 translate-middle">
          <CenteredSpinner />
        </div>
      </td>
    }
    renderData={ots =>
      ots.length === 0
        ? <td colSpan={7} className="justify-content-center">
          <h3 className="text-center">No Order Transactions</h3>
        </td>
        : <td colSpan={7}>
          <ul className="tree">
            <Row className="mb-1">
              <Col xs={2}><span className="text-dark fw-semibold">Transactions</span></Col>
              <Col xs={2} />
              <Col xs={2} className="text-end"><span className="text-dark fw-semibold">Order Quantity</span></Col>
              <Col xs={2} className="text-end"><span className="text-dark fw-semibold">Dispensed Quantity</span></Col>
              <Col xs={2} className="text-end"><span className="text-dark fw-semibold">Completed Date</span></Col>
            </Row>
            {
              dateSort(ots, 'loadedAt').map(ot =>
                <li key={ot.id}>
                  <Row>
                    <Col xs={2} className="ms-1">
                      <button type="button" className="btn btn-link p-0" onClick={() => transactionSelected(ot.id)}>
                        {ot.number}
                      </button>
                    </Col>
                    <Col xs={2}>
                      <span
                        className={`${ot.status === TransactionStatus.Completed ? 'text-primary' : 'text-danger'} fw-semibold`}>
                        {ot.status}
                      </span>
                    </Col>
                    <Col xs={2} className="text-end">
                      <AmountDisplay amount={requested} hoverId={`requested-${ot.id}`} />
                    </Col>
                    <Col xs={2} className="text-end">
                      <AmountDisplay amount={ot.totalDispensed} hoverId={`dispensed-${ot.id}`} />
                    </Col>
                    <Col xs={2} className="text-end">{new Date(ot.loadedAt).toLocaleString()}</Col>
                  </Row>
                </li>
              )
            }
          </ul>
          <Row className="mt-3">
            <Totals requested={requested} ots={ots} orderId={orderId} />
          </Row>
        </td>
    }
  />
}

const OrderColumn = ({ children }: { children: JSX.Element }) =>
  <td className="py-2 align-middle">
    {children}
  </td>

const OrderPage = () => {
  const [viewCompletedOrders, setViewCompletedOrders] = useState(false);
  const [assignSitesOrder, setAssignSitesOrder] = useState<AssignSitesOrderDto | undefined>();

  const [numberSearch, setNumberSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [sortMode, setSortMode] = useState(defaultSort);

  const [filters, setFilters] = useState<FilterSettings>({
    item: '',
    customer: '',
    startDate: '',
    endDate: '',
  });

  const siteFilter = useSiteFilter();

  const updateFilters = (newFilters: Partial<FilterSettings>) => {
    setFilters({ ...filters, ...newFilters });
    setCurrentPage(0);
    setSelectedOrderIds([]);
  };

  const sitesChanged = (values: string[]) => {
    siteFilter.setValues(values);
    setCurrentPage(0);
    setSelectedOrderIds([]);
  };

  const user = useUser();
  const getProducts = useGetProducts();
  const getRecipes = useGetRecipes();
  const getCustomers = useGetCustomers();

  const getOrders = useSearchOrders({
    completed: viewCompletedOrders,
    page: currentPage,
    pageSize: pageSize,
    orderNumberFilter: debouncedSearch,
    sortField: SortField[sortMode.field],
    sortDirection: SortDirection[sortMode.direction],
    itemId: filters.item,
    customerId: filters.customer,
    siteIds: siteFilter.siteIds,
    unassignedSites: siteFilter.includeUnassigned,
    startDate: filters.startDate,
    endDate: filters.endDate,
  }, {
    onSuccess: (paginatedOrders) => {
      if (!paginatedOrders) return;

      if (assignSitesOrder) assignSitesClick(assignSitesOrder);
    }
  });

  const [confirmOrderCompletion, setConfirmOrderCompletion] = useState(false);
  const [orderToComplete, setOrderToComplete] = useState<OrderDisplayDto | undefined>(undefined);
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);
  const [orderToRemove, setOrderToRemove] = useState<OrderDisplayDto | undefined>(undefined);
  const [showSiteAssignmentForm, setShowSiteAssignmentForm] = useState(false);
  const displayMessage = useDisplayMessage();
  const logException = useLogException();
  const { orderNumber: initialOrderNumber, completed: initialCompleted } = useParams();

  const [createEditMode, setCreateEditMode] = useState<CreateEditMode | null>(null);
  const [showTransactions, setShowTransactions] = useState<ShowTransactions>(undefined);
  const [createTransactionOrder, setCreateTransactionOrder] = useState<OrderDisplayDto | undefined>(undefined);

  const transactionOrders = useToggleableItems();

  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [showConfirmBulkRemove, setShowConfirmBulkRemove] = useState(false);
  const [showConfirmBulkComplete, setShowConfirmBulkComplete] = useState(false);

  const canModifyOrders = usePermission('ordersModify');
  const canBulkSelect = usePermission('ordersComplete') || canModifyOrders;

  useEffect(() => {
    if (initialOrderNumber) {
      numberSearchChanged(initialOrderNumber);
    }

    if (initialCompleted === 'completed') {
      handleViewCompletedOrders(true);
    }
  }, [initialOrderNumber, initialCompleted]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(numberSearch);
    }, 600);

    return () => clearTimeout(handler);
  }, [numberSearch]);

  const numberSearchChanged = (number: string) => {
    setCurrentPage(0);
    setNumberSearch(number);
    setSelectedOrderIds([]);
  };

  const handlePageChange = (selected: number) => {
    setCurrentPage(selected);
    setSelectedOrderIds([]);
  };

  const handleViewCompletedOrders = (viewCompleted: boolean) => {
    setCurrentPage(0);
    setSelectedOrderIds([]);

    if ((!viewCompleted && sortMode.field === SortField.completedAt) ||
      (viewCompleted && sortMode.field === SortField.createdAt)) {
      setSortMode({ ...defaultSort });
    }

    setViewCompletedOrders(viewCompleted);
  };

  const handleSortMode = (sortField: SortField) => {
    setSortMode((prev) => {
      const isSameFieldSelected = prev.isHighlighted && prev.field === sortField;

      return {
        isHighlighted: true,
        field: sortField,
        direction: isSameFieldSelected && prev.direction === SortDirection.ascending
          ? SortDirection.descending
          : SortDirection.ascending
      };
    });
  };

  const sortFieldMap: Record<string, SortField> = {
      Number: SortField.number,
      Created: SortField.createdAt,
      Completed: SortField.completedAt,
    };

  const isSortColumnHighlighted = (sortField: SortField) =>
    sortMode.isHighlighted && sortField === sortMode.field;

  const createEditSuccess = (orderId: string, orderNumber: string, assignSites: boolean) => {
    displayMessage.success(`Order ${orderNumber} ${createEditMode?.create ? 'created' : 'updated'}`);
    editingOrderCompleted();
    if (assignSites) setAssignSitesOrder({ id: orderId, number: orderNumber });
    getOrders.query();
  }

  const editingOrderCompleted = () => {
    setCreateEditMode(null);
  }

  const completeRequest = useUserRequest(
    completeRequestSpec(user.selectedBusiness?.id, orderToComplete?.id), {
    onSuccess: () => getOrders.query(),
    onError: (err) => displayMessage.fail(err.message),
    onComplete: () => setConfirmOrderCompletion(false),
  });

  const removeSuccess = () => {
    displayMessage.success(`Order ${orderToRemove?.number} removed`);
    return getOrders.query();
  };

  const removeRequest = useUserRequest(
    removeRequestSpec(user.selectedBusiness?.id, orderToRemove?.id), {
    onSuccess: removeSuccess,
    onError: (err) => displayMessage.fail(err.message),
    onComplete: () => setShowConfirmRemove(false),
  });

  const createClick = () => {
    displayMessage.clear();
    setCreateEditMode({ create: true });
  }

  const isComplete = (order: OrderDto) => order.stage === 'Complete';

  const isReadOnly = (order: OrderDto) => order.hasTransactions || isComplete(order);

  const getOrderItems = (order: OrderDto) => order.blends.flatMap(b => b.items.map(i => i.name))

  const editClick = (order: OrderDisplayDto) => {
    displayMessage.clear();
    setCreateEditMode({ create: false, initialOrder: order });
  }

  const assignSitesClick = (assignSitesOrder: AssignSitesOrderDto) => {
    displayMessage.clear();
    setAssignSitesOrder({ id: assignSitesOrder.id, number: assignSitesOrder.number });
    setShowSiteAssignmentForm(true);
  }

  const assignSitesSuccess = () => {
    displayMessage.success(`Assigned ${assignSitesOrder?.number} to the selected sites`);
    assignSitesClosed();
    getOrders.query();
  }

  const assignSitesClosed = () => {
    setShowSiteAssignmentForm(false);
    setAssignSitesOrder(undefined);
  }

  const complete = (order: OrderDisplayDto) => {
    setOrderToComplete(order);
    setConfirmOrderCompletion(true);
  };

  const confirmOrderCompletionDismissed = () => setConfirmOrderCompletion(false);

  const orderCompletionConfirmed = () => completeRequest.request(null);

  const remove = (order: OrderDisplayDto) => {
    setOrderToRemove(order);
    setShowConfirmRemove(true);
  };

  const dismissConfirmRemove = () => setShowConfirmRemove(false);

  const removeConfirmed = () => removeRequest.request(null);

  const showOrderTransactionClick = (order: OrderDisplayDto) => setShowTransactions({ order });

  const createTransactionSuccess = () => {
    displayMessage.success(`Created transaction for order ${createTransactionOrder?.number}`);
    setCreateTransactionOrder(undefined);
    getOrders.query();
  };

  const setUnrecoverableDataError = (exception: Error, severityLevel?: SeverityLevel) => {
    editingOrderCompleted();
    displayMessage.fail("Something is wrong with this order. Please contact Kahler Automation.");
    logException(exception, severityLevel);
  }

  const itemOptions = toKaSelectOptions<ProductDto | RecipeDto, KaSelectOption>(
    [...(getProducts.data ?? []), ...(getRecipes.data ?? [])],
    "id",
    "name",
    { sortByLabel: true }
  );

  const customerOptions = toKaSelectOptions(getCustomers.data ?? [],
    "id",
    "name",
    {
      labelFn: customerDisplayNameFromDto,
      sortByLabel: true
    });


  const createdOrCompleted = viewCompletedOrders ? 'Completed' : 'Created';

  const toggleBulkSelectMode = () => {
    setBulkSelectMode(!bulkSelectMode);
    setSelectedOrderIds([]);
  };

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrderIds(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const toggleSelectAll = (orders: OrderDisplayDto[]) => {
    const allSelected = orders.every(o => selectedOrderIds.includes(o.id));
    setSelectedOrderIds(allSelected ? [] : orders.map(o => o.id));
  };

  const bulkRemoveSuccess = (response: BulkRemoveOrdersResponse) => {
    const removedCount = response.removed.length;
    const failedCount = response.failed.length;

    if (removedCount > 0 && failedCount === 0) {
      displayMessage.success(`Successfully removed ${removedCount} order(s)`);
    } else if (removedCount > 0 && failedCount > 0) {
      const failedDetails = response.failed.map(f => `${f.number}: ${f.reason}`).join('\n');
      displayMessage.fail(`Removed ${removedCount} order(s). Failed to remove:\n${failedDetails}`);
    } else {
      const failedDetails = response.failed.map(f => `${f.number}: ${f.reason}`).join('\n');
      displayMessage.fail(`Could not remove orders:\n${failedDetails}`);
    }

    setSelectedOrderIds([]);
    setShowConfirmBulkRemove(false);
    getOrders.query();
  };

  const bulkRemoveRequest = useBulkRemoveOrders({
    onSuccess: bulkRemoveSuccess,
    onError: (err) => {
      displayMessage.fail(err.message);
      setShowConfirmBulkRemove(false);
    },
  });

  const confirmBulkRemove = () => {
    bulkRemoveRequest.request({ orderIds: selectedOrderIds });
  };

  const bulkCompleteSuccess = (response: BulkCompleteOrdersResponse) => {
    const completedCount = response.completed.length;
    const failedCount = response.failed.length;

    if (completedCount > 0 && failedCount === 0) {
      displayMessage.success(`Successfully completed ${completedCount} order(s)`);
    } else if (completedCount > 0 && failedCount > 0) {
      const failedDetails = response.failed.map(f => `${f.number}: ${f.reason}`).join('\n');
      displayMessage.fail(`Completed ${completedCount} order(s). Failed to complete:\n${failedDetails}`);
    } else {
      const failedDetails = response.failed.map(f => `${f.number}: ${f.reason}`).join('\n');
      displayMessage.fail(`Could not complete orders:\n${failedDetails}`);
    }

    setSelectedOrderIds([]);
    setShowConfirmBulkComplete(false);
    getOrders.query();
  };

  const bulkCompleteRequest = useBulkCompleteOrders({
    onSuccess: bulkCompleteSuccess,
    onError: (err) => {
      displayMessage.fail(err.message);
      setShowConfirmBulkComplete(false);
    },
  });

  const confirmBulkComplete = () => {
    bulkCompleteRequest.request({ orderIds: selectedOrderIds });
  };

  return <div>
    <Confirm
      visible={confirmOrderCompletion}
      title='Complete Order'
      body={`When an order is completed it is no longer available to dispense.
               Completion cannot be undone.
               Are you sure that you want to complete order ${orderToComplete?.number}?`}
      isLoading={completeRequest.isLoading}
      onDismiss={confirmOrderCompletionDismissed}
      onConfirm={orderCompletionConfirmed} />
    <Confirm
      visible={showConfirmRemove}
      title='Remove Order'
      body={`When an order is removed it is no longer available to dispense.
               The order cannot be restored.
               Order transactions are not removed.
               Are you sure that you want to remove order ${orderToRemove?.number}?`}
      isLoading={removeRequest.isLoading}
      onDismiss={dismissConfirmRemove}
      onConfirm={removeConfirmed} />
    <Confirm
      visible={showConfirmBulkRemove}
      title='Remove Selected Orders'
      body={`You are about to remove ${selectedOrderIds.length} order(s).
               Removed orders are no longer available to dispense and cannot be restored.
               Order transactions are not removed.
               Are you sure you want to proceed?`}
      isLoading={bulkRemoveRequest.isLoading}
      onDismiss={() => setShowConfirmBulkRemove(false)}
      onConfirm={confirmBulkRemove} />
    <Confirm
      visible={showConfirmBulkComplete}
      title='Complete Selected Orders'
      body={`You are about to complete ${selectedOrderIds.length} order(s).
               Completed orders are no longer available to dispense.
               Completion cannot be undone.
               Are you sure you want to proceed?`}
      isLoading={bulkCompleteRequest.isLoading}
      onDismiss={() => setShowConfirmBulkComplete(false)}
      onConfirm={confirmBulkComplete} />
    {showSiteAssignmentForm &&
      <SiteAssignmentForm
        title={`Select sites for order ${assignSitesOrder?.number}`}
        getAssignedSitesPath={apiGetSiteAssignmentPath(user.selectedBusiness?.id, assignSitesOrder?.id)}
        assignSitePath={apiSetSiteAssignmentPath(user.selectedBusiness?.id, assignSitesOrder?.id)}
        viewOnly={!canModifyOrders}
        onHide={assignSitesClosed}
        onSuccess={assignSitesSuccess} />
    }
    {createEditMode !== null ? (
      <CreateEditOrder
        onSuccess={createEditSuccess}
        user={user}
        mode={createEditMode}
        readOnly={!createEditMode.create && (isReadOnly(createEditMode.initialOrder) || !canModifyOrders)}
        onCancelled={editingOrderCompleted}
        setUnrecoverableDataError={setUnrecoverableDataError}
      />
    ) : showTransactions ? (
      <OrderTransactionPage
        profile={user}
        order={showTransactions.order}
        orderTransactionId={showTransactions.orderTransactionId}
        backClicked={() => setShowTransactions(undefined)}
      />
    ) : (
      <>
        <Row className="justify-content-between mb-2">
          <h2 className="col-auto ka-blue">Orders</h2>
          <div className="col-auto d-flex gap-2 align-items-center">
            {bulkSelectMode && (
              <>
                <span className="text-muted">{selectedOrderIds.length} order(s) selected</span>
                {!viewCompletedOrders && (
                  <PermissionButton
                    permission="ordersComplete"
                    className="btn-ghost-primary"
                    disabled={selectedOrderIds.length === 0}
                    onClick={() => setShowConfirmBulkComplete(true)}>
                    Complete Selected
                  </PermissionButton>
                )}
                <PermissionButton
                  permission="ordersModify"
                  className="btn-ghost-danger"
                  disabled={selectedOrderIds.length === 0}
                  onClick={() => setShowConfirmBulkRemove(true)}>
                  Remove Selected
                </PermissionButton>
              </>
            )}
            <GatedButton
              allowed={canBulkSelect}
              className={`btn-ghost-${bulkSelectMode ? 'secondary' : 'primary'}`}
              onClick={toggleBulkSelectMode}>
              {bulkSelectMode ? 'Cancel' : 'Bulk Select'}
            </GatedButton>
            {!bulkSelectMode && (
              <PermissionButton permission="ordersModify" className="btn-ghost-primary" onClick={createClick}>
                Create Order
              </PermissionButton>
            )}
          </div>
        </Row>
        <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
        <form>
          <div className="row gx-2 gx-md-3 mb-3">
            <div className="col-md-4 mb-2 mb-md-0">
              <FilterInput placeholder='Search by number' value={numberSearch} onFilterTextChange={numberSearchChanged} />
            </div>
          </div>
        </form>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <Toggle
            value={viewCompletedOrders}
            offLabel="Active"
            onLabel="Completed"
            onChange={handleViewCompletedOrders} />
          <div className="vr filter-pill-divider mx-1" />
          <FilterPillGroup>
            <SiteFilterPill key="order-sites" filter={{ ...siteFilter, setValues: sitesChanged }} debounceMs={400} />
            <SelectPill
              key="order-item"
              id="order-item"
              label="Product/Recipe"
              icon="diagram-3"
              value={filters.item || undefined}
              options={itemOptions}
              onChange={value => updateFilters({ item: value ?? '' })} />
            <SelectPill
              key="order-customer"
              id="order-customer"
              label="Customer"
              icon="people"
              value={filters.customer || undefined}
              options={customerOptions}
              onChange={value => updateFilters({ customer: value ?? '' })} />
            <DateRangePill
              key="order-dates"
              id="order-dates"
              label={createdOrCompleted}
              icon="calendar3"
              range={filters.startDate && filters.endDate
                ? { start: filters.startDate, end: filters.endDate }
                : undefined}
              onChange={range => updateFilters({
                startDate: range?.start ?? '',
                endDate: range?.end ?? '',
              })} />
          </FilterPillGroup>
        </div>
        <QueryView query={getOrders}
          renderLoading={() => <div className="mt-8">
            <CenteredSpinner />
          </div>}
          renderData={paginatedOrders => {
            return <PaginatedTable
              onPageChange={handlePageChange}
              currentPage={currentPage}
              pageSize={pageSize}
              totalPages={paginatedOrders.totalPages}>
              <Table borderless className="mt-5">
                <thead>
                  <tr className="border-bottom border-dark border-1 align-top">
                    {bulkSelectMode && (
                      <th className="px-2" style={{ verticalAlign: 'middle', width: '1%' }}>
                        <input
                          type="checkbox"
                          style={{ width: '24px', height: '24px', verticalAlign: 'middle' }}
                          checked={paginatedOrders.items.length > 0 && paginatedOrders.items.every(o => selectedOrderIds.includes(o.id))}
                          onChange={() => toggleSelectAll(paginatedOrders.items)}
                        />
                      </th>
                    )}
                    <th className="col-1">
                      <SortableColumnHeader
                        name="Number"
                        isActive={isSortColumnHighlighted(SortField.number)}
                        direction={sortMode.direction}
                        handleSort={() => handleSortMode(SortField.number)}
                      />
                    </th>
                    <th className="col-2">Customer</th>
                    <th className="col-3">Products/Recipes</th>
                    <SiteColumnHeader />
                    <th className="col-1">
                      <SortableColumnHeader
                        name={createdOrCompleted}
                        isActive={isSortColumnHighlighted(sortFieldMap[createdOrCompleted])}
                        direction={sortMode.direction}
                        handleSort={() => handleSortMode(sortFieldMap[createdOrCompleted])}
                      />
                    </th>
                    <th className="col-1 ps-0">
                      {bulkSelectMode && !viewCompletedOrders ? 'Completion' : ''}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.items
                    .map(order =>
                      <React.Fragment key={order.id}>
                        <tr className={`border-top ${selectedOrderIds.includes(order.id) ? 'bg-light' : ''}`}>
                          {bulkSelectMode && (
                            <td className="px-2 align-middle" style={{ width: '1%' }}>
                              <input
                                type="checkbox"
                                style={{ width: '24px', height: '24px', verticalAlign: 'middle' }}
                                checked={selectedOrderIds.includes(order.id)}
                                onChange={() => toggleOrderSelection(order.id)}
                              />
                            </td>
                          )}
                          <OrderColumn>
                            <p className="mb-0 preserve-whitespace">
                              {order.number}
                            </p>
                          </OrderColumn>
                          <OrderColumn>
                            <OverflowTooltip
                              className="mb-0"
                              text={customerDisplayName(order.customerName, order.customerAccountNumber)} />
                          </OrderColumn>
                          <OrderColumn>
                            <OverflowTooltip className="mb-0" text={getOrderItems(order).join(", ")} />
                          </OrderColumn>
                          <SiteColumnCell siteNames={order.siteNames} />
                          <OrderColumn>
                            <p className="mb-0">
                              {(() => {
                                const date = viewCompletedOrders ? order.completedAt : order.createdAt;
                                return date ? new Date(date).toLocaleDateString() : '';
                              })()}
                            </p>
                          </OrderColumn>
                          <OrderColumn>
                            <Row className="align-items-center">
                              <Col xs={9} className="px-0">
                                {bulkSelectMode && !viewCompletedOrders ? (
                                  (() => {
                                    const requested = order.totalRequested.quantity ?? 0;
                                    const dispensed = order.totalDispensed.quantity ?? 0;
                                    const percentage = requested === 0 ? 0 : Math.min(dispensed / requested * 100, 100);
                                    return (
                                      <div className="d-flex align-items-center gap-2">
                                        <div className="progress flex-grow-1" style={{ height: '6px' }}>
                                          <div
                                            className="progress-bar bg-primary"
                                            role="progressbar"
                                            style={{ width: `${percentage}%` }}
                                            aria-valuenow={percentage}
                                          />
                                        </div>
                                        <span className="text-muted text-end" style={{ width: '50px', flexShrink: 0 }}>{percentage.toFixed(1)}%</span>
                                      </div>
                                    );
                                  })()
                                ) : (
                                  <div className="dropdown">
                                    <Button
                                      className="dropdown-toggle btn-ghost-secondary"
                                      variant="link"
                                      id="actionsMenuButton"
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                      style={{ marginTop: '3px' }}>
                                      <strong>Actions</strong>
                                    </Button>
                                    <div className="dropdown-menu" aria-labelledby="actionsMenuButton">
                                      <BoldButton className="dropdown-item btn-ghost-secondary" onClick={() => editClick(order)}>
                                        {isReadOnly(order) || !canModifyOrders ? 'View' : 'Edit'}
                                      </BoldButton>
                                      {!isComplete(order) && <PermissionButton permission="ordersComplete" className="dropdown-item btn-ghost-secondary" onClick={() => complete(order)}>Complete</PermissionButton>}
                                      <PermissionButton permission="ordersModify" className="dropdown-item btn-ghost-secondary" onClick={() => remove(order)}>Remove</PermissionButton>
                                      <BoldButton className="dropdown-item btn-ghost-secondary" onClick={() => assignSitesClick(order)}>{canModifyOrders ? 'Assign Sites' : 'View Sites'}</BoldButton>
                                      {order.hasTransactions && <BoldButton className="dropdown-item btn-ghost-secondary" onClick={() => showOrderTransactionClick(order)}>View Transactions</BoldButton>}
                                      {user.isDevelopment && <BoldButton className="dropdown-item btn-ghost-secondary" onClick={() => setCreateTransactionOrder(order)}>Create Transaction</BoldButton>}
                                    </div>
                                  </div>
                                )}
                              </Col>
                              <Col xs={3} className={`px-0 d-block ${bulkSelectMode && !viewCompletedOrders ? 'ps-1' : ''}`}>
                                {(!bulkSelectMode || order.hasTransactions) && (
                                  <button
                                    type="button"
                                    className="btn btn-icon btn-ghost-primary btn-link"
                                    data-testid={`showTransactions-${order.id}`}
                                    onClick={() => transactionOrders.toggle(order.id)}>
                                    {<i className={transactionOrders.isEnabled(order.id) ? 'bi-caret-up-square' : 'bi-caret-down-square'} />}
                                  </button>
                                )}
                              </Col>
                            </Row>
                          </OrderColumn>
                        </tr>
                        <Collapse isOpen={transactionOrders.isEnabled(order.id)} tag={'tr'} className={`border-start border-end ${selectedOrderIds.includes(order.id) ? 'bg-light' : ''}`}>
                          {bulkSelectMode && <td />}
                          <OrderTransactions
                            orderId={order.id}
                            requested={order.totalRequested}
                            transactionSelected={otId => setShowTransactions({ order, orderTransactionId: otId })}
                            show={transactionOrders.isEnabled(order.id)} />
                        </Collapse>
                      </React.Fragment>
                    )}
                  {paginatedOrders.items.length === 0 &&
                    <tr>
                      <td colSpan={bulkSelectMode ? 7 : 6} className="text-center text-muted py-3">
                        {siteFilter.emptyMessage('No orders to show')}
                      </td>
                    </tr>}
                </tbody>
              </Table>
            </PaginatedTable>
          }} />
        {createTransactionOrder && (
          <CreateTransactionModal
            order={createTransactionOrder}
            onHide={() => setCreateTransactionOrder(undefined)}
            onSuccess={createTransactionSuccess}
          />
        )}
      </>
    )}
  </div >;
}

export { OrderPage };
